import {
  TIME_REPORT_GROUPS,
  TIME_REPORT_PERIODS,
  type TimeReportGroup,
  type TimeReportInput,
  type TimeReportPeriod,
} from '@fos/shared/contracts/time';
import { addDaysLocal, isLocalDate, todayLocal } from '@fos/shared/time';
import { useSearchParams } from 'react-router';
import type { Scope } from '../../../lib/scope';
import { exportUrl } from '../time-format';

/**
 * Stan raportu w adresie (`/czas?widok=raport&okres=last_month&klient=…`): link do raportu można wysłać,
 * a przyciski CSV / PDF w nagłówku strony używają tych samych parametrów co tabela.
 */

/** Widoki /czas: wpisy, raport, rozliczenia partnera (`?widok=rozliczenia`). */
export type TimeView = 'wpisy' | 'raport' | 'rozliczenia';

export interface ReportParams {
  period: TimeReportPeriod;
  from: string;
  to: string;
  clientId: string | null | undefined;
  projectId: string | null | undefined;
  userId: string | undefined;
  group: TimeReportGroup;
}

const NONE = 'brak';

function idParam(v: string | null): string | null | undefined {
  if (!v) return undefined;
  return v === NONE ? null : v;
}

export function readReportParams(params: URLSearchParams, today = todayLocal()): ReportParams {
  const period = params.get('okres');
  const group = params.get('grupuj');
  const from = params.get('od');
  const to = params.get('do');
  return {
    period: TIME_REPORT_PERIODS.includes(period as TimeReportPeriod) ? (period as TimeReportPeriod) : 'this_month',
    from: from && isLocalDate(from) ? from : addDaysLocal(today, -29),
    to: to && isLocalDate(to) ? to : today,
    clientId: idParam(params.get('klient')),
    projectId: idParam(params.get('projekt')),
    userId: params.get('osoba') || undefined,
    group: TIME_REPORT_GROUPS.includes(group as TimeReportGroup) ? (group as TimeReportGroup) : 'project',
  };
}

export function useTimeView(): [TimeView, (v: TimeView) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get('widok');
  const view: TimeView = raw === 'raport' || raw === 'rozliczenia' ? raw : 'wpisy';
  const setView = (v: TimeView) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v !== 'wpisy') next.set('widok', v);
        else next.delete('widok');
        return next;
      },
      { replace: true },
    );
  return [view, setView];
}

export function useReportParams(): [ReportParams, (patch: Partial<ReportParams>) => void] {
  const [params, setParams] = useSearchParams();
  const value = readReportParams(params);
  const patch = (p: Partial<ReportParams>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const put = (key: string, v: string | null | undefined, def?: string) => {
          if (v === undefined || v === def) next.delete(key);
          else next.set(key, v === null ? NONE : v);
        };
        if ('period' in p) put('okres', p.period, 'this_month');
        if ('from' in p) put('od', p.from);
        if ('to' in p) put('do', p.to);
        if ('clientId' in p) put('klient', p.clientId);
        if ('projectId' in p) put('projekt', p.projectId);
        if ('userId' in p) put('osoba', p.userId);
        if ('group' in p) put('grupuj', p.group, 'project');
        return next;
      },
      { replace: true },
    );
  return [value, patch];
}

/** Wejście `time.report` z parametrów (osoba tylko w widoku Zespół). */
export function reportInput(p: ReportParams, scope: Scope): TimeReportInput {
  return {
    period: p.period,
    ...(p.period === 'custom' ? { from: p.from, to: p.to } : {}),
    scope,
    ...(p.clientId !== undefined ? { clientId: p.clientId } : {}),
    ...(p.projectId !== undefined ? { projectId: p.projectId } : {}),
    ...(scope === 'team' && p.userId ? { userId: p.userId } : {}),
  };
}

export function reportExportUrl(format: 'csv' | 'pdf', p: ReportParams, scope: Scope): string {
  return exportUrl(format, {
    period: p.period,
    from: p.from,
    to: p.to,
    scope,
    clientId: p.clientId,
    projectId: p.projectId,
    userId: scope === 'team' ? p.userId : undefined,
  });
}
